import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff, Copy, Check, ShieldAlert, Clock, ArrowRight, Trash2, Download, ExternalLink, Loader2, KeyRound, Mail, ShieldCheck } from 'lucide-react';
import { getPushPreview, revealPush, deletePush, requestPushEmailCode, verifyPushEmailCode, downloadPushFile } from '../lib/api';
import { toast } from 'sonner';

export default function ViewPush() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [content, setContent] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [showText, setShowText] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Email OTP state
  const [emailStep, setEmailStep] = useState('input'); // 'input' | 'code' | 'verified'
  const [emailInput, setEmailInput] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [revealToken, setRevealToken] = useState(null);
  const [debugCode, setDebugCode] = useState(null); // shown when email provider is mock

  useEffect(() => {
    (async () => {
      try {
        const m = await getPushPreview(token);
        setMeta(m);
      } catch (e) { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const brand = meta?.brand;
  const primary = brand?.primary_color || '#0d63b0';
  const accent = brand?.accent_color || '#1e88d6';

  const requestCode = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailInput.trim())) { toast.error('Inserisci una email valida'); return; }
    setEmailLoading(true);
    setDebugCode(null);
    try {
      const res = await requestPushEmailCode(token, emailInput.trim().toLowerCase());
      if (res.debug_code) setDebugCode(res.debug_code);
      setEmailStep('code');
      toast.success(res.debug_code ? 'Codice generato (demo mode)' : 'Codice inviato via email', { description: res.note });
    } catch (e) {
      const d = e?.response?.data?.detail || 'Errore';
      if (d === 'email_not_authorized') toast.error('Questa email non è autorizzata');
      else if (d === 'too_many_requests') toast.error('Troppi tentativi, riprova tra qualche minuto');
      else toast.error('Errore', { description: d });
    } finally { setEmailLoading(false); }
  };

  const verifyCode = async () => {
    if (emailCode.trim().length < 4) { toast.error('Inserisci il codice ricevuto'); return; }
    setEmailLoading(true);
    try {
      const res = await verifyPushEmailCode(token, emailInput.trim().toLowerCase(), emailCode.trim());
      setRevealToken(res.reveal_token);
      setEmailStep('verified');
      toast.success('Email verificata');
    } catch (e) {
      const d = e?.response?.data?.detail || 'Errore';
      if (d === 'invalid_code') toast.error('Codice errato');
      else if (d === 'too_many_attempts') toast.error('Troppi tentativi errati');
      else if (d === 'code_not_found_or_expired') toast.error('Codice scaduto, richiedi un nuovo codice');
      else toast.error('Errore', { description: d });
    } finally { setEmailLoading(false); }
  };

  const doReveal = async () => {
    setRevealing(true);
    try {
      const res = await revealPush(token, passphrase, revealToken);
      // For file kind, receive download_reveal_token instead of content
      if (res.kind === 'files') {
        setContent(res);
        setRevealed(true);
        toast.success('File pronto per il download');
      } else {
        setContent(res);
        setRevealed(true);
        toast.success('Contenuto rivelato');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Errore';
      if (detail === 'invalid_passphrase') toast.error('Passphrase errata');
      else if (detail === 'email_verification_required') toast.error('Verifica prima la tua email');
      else if (detail === 'invalid_reveal_token') toast.error('Sessione scaduta, ripeti la verifica email');
      else if (detail === 'expired' || detail === 'not_found') { setNotFound(true); toast.error('Push scaduto o non trovato'); }
      else toast.error(detail);
    } finally { setRevealing(false); }
  };

  const copyContent = async () => {
    try {
      await navigator.clipboard.writeText(content.content);
      setCopied(true);
      toast.success('Copiato negli appunti');
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error('Impossibile copiare'); }
  };

  const doDelete = async () => {
    if (!confirm('Distruggere questo push ora?')) return;
    try { await deletePush(token); toast.success('Push distrutto'); setNotFound(true); }
    catch { toast.error('Errore'); }
  };

  const downloadFile = async () => {
    if (!content?.download_reveal_token) return;
    try {
      const resp = await downloadPushFile(token, content.download_reveal_token);
      const blob = resp.data;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = content.file_name || 'download';
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Download avviato');
    } catch (e) {
      toast.error('Errore download', { description: e?.response?.data?.detail || '' });
    }
  };

  if (loading) return (
    <div className="tv-gradient-soft min-h-[70vh] flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-[#0d63b0]" />
    </div>
  );

  if (notFound) return (
    <div className="tv-gradient-soft min-h-[70vh] flex items-center justify-center py-16">
      <Card className="max-w-md w-full mx-4 p-8 text-center border border-slate-200">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600 border border-red-100"><ShieldAlert className="h-7 w-7" /></div>
        <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Push non disponibile</h1>
        <p className="mt-2 text-slate-600 text-sm">Il link potrebbe essere già stato visualizzato il numero massimo di volte, essere scaduto o eliminato.</p>
        <Button asChild className="mt-6 bg-[#0d63b0] hover:bg-[#0a4f8f]">
          <Link to="/p/new">Crea un nuovo push <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
        </Button>
      </Card>
    </div>
  );

  const kindLabel = meta.kind === 'text' ? 'Testo riservato' : meta.kind === 'files' ? 'File riservato' : 'URL riservato';
  const emailGated = meta.requires_email_verification;
  const needsEmailAuth = emailGated && emailStep !== 'verified';

  return (
    <div className="py-12 min-h-[70vh]" style={{ background: `linear-gradient(180deg, ${accent}12, #ffffff 60%)` }}>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <Card className="border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            {brand?.logo_url ? (
              <img src={brand.logo_url} alt={brand.name} className="h-10 w-auto max-w-[160px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}><Lock className="h-5 w-5" /></div>
            )}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: primary }}>{brand?.name || 'Secure Push'}</div>
              <h1 className="text-lg font-bold text-slate-900">{kindLabel}</h1>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1"><Clock className="h-3 w-3" /> Scade tra {meta.days_left} {meta.days_left === 1 ? 'giorno' : 'giorni'}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2.5 py-1"><Eye className="h-3 w-3" /> {meta.views_left} vis. rimaste</span>
            {meta.requires_passphrase && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1"><KeyRound className="h-3 w-3" /> Passphrase</span>}
            {emailGated && <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-1"><Mail className="h-3 w-3" /> Verifica email richiesta</span>}
            {meta.file_size && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2.5 py-1">{(meta.file_size / 1024 / 1024).toFixed(2)} MB</span>}
          </div>

          {/* Email OTP flow */}
          {!revealed && needsEmailAuth && (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-5">
                <div className="flex items-center gap-2 text-purple-900 font-semibold"><Mail className="h-5 w-5" /> Verifica destinatario</div>
                <p className="mt-1 text-sm text-purple-800">Questo push è riservato a specifici destinatari. Inserisci la tua email per ricevere un codice di accesso.</p>
              </div>
              {emailStep === 'input' && (
                <div>
                  <Label className="text-sm text-slate-700">La tua email</Label>
                  <Input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); requestCode(); } }}
                    placeholder="tuo@indirizzo.it" className="mt-1" />
                  <Button onClick={requestCode} disabled={emailLoading} className="mt-3 w-full text-white h-11" style={{ backgroundColor: primary }}>
                    {emailLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Mail className="h-4 w-4 mr-1.5" /> Ricevi codice</>)}
                  </Button>
                </div>
              )}
              {emailStep === 'code' && (
                <div>
                  <div className="text-xs text-slate-500 mb-2">Codice inviato a <strong className="text-slate-800">{emailInput}</strong></div>
                  {debugCode && (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Modalità DEMO</div>
                      <div className="mt-1 text-amber-900">Nessun provider email configurato. Codice: <span className="font-mono font-bold text-lg">{debugCode}</span></div>
                    </div>
                  )}
                  <Label className="text-sm text-slate-700">Codice di 6 cifre</Label>
                  <Input value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); verifyCode(); } }}
                    placeholder="123456" className="mt-1 font-mono text-lg tracking-widest text-center" maxLength={6} />
                  <div className="mt-3 flex gap-2">
                    <Button onClick={() => { setEmailStep('input'); setEmailCode(''); setDebugCode(null); }} variant="outline" className="flex-1">Cambia email</Button>
                    <Button onClick={verifyCode} disabled={emailLoading} className="flex-1 text-white" style={{ backgroundColor: primary }}>
                      {emailLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verifica codice'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!revealed && !needsEmailAuth && (
            <div className="mt-6">
              {emailStep === 'verified' && (
                <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex items-center gap-2 text-sm text-emerald-800">
                  <ShieldCheck className="h-4 w-4" /> Email verificata: <strong>{emailInput}</strong>
                </div>
              )}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center">
                <Lock className="h-8 w-8 mx-auto text-slate-400" />
                <div className="mt-3 font-mono text-slate-400 tracking-widest text-lg select-none">••••••••••••••••</div>
                <p className="mt-3 text-sm text-slate-600">Il contenuto sarà visibile solo dopo la conferma. Verrà conteggiata una visualizzazione.</p>
              </div>
              {meta.requires_passphrase && (
                <div className="mt-4">
                  <Label className="text-sm text-slate-700">Passphrase</Label>
                  <Input value={passphrase} onChange={(e) => setPassphrase(e.target.value)} type="text" placeholder="Inserisci la parola d'ordine" className="mt-1" />
                </div>
              )}
              <Button onClick={doReveal} disabled={revealing} size="lg" className="mt-4 w-full text-white h-11" style={{ backgroundColor: primary }}>
                {revealing ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Verifica in corso...</>) : (<><Eye className="h-4 w-4 mr-1.5" /> Rivela contenuto</>)}
              </Button>
              <div className="mt-3 text-center text-xs text-slate-500">Contenuto cifrato AES-256 · Auto-eliminazione</div>
            </div>
          )}

          {revealed && content && (
            <div className="mt-6">
              {meta.kind === 'text' && (
                <div>
                  <div className="relative">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-sm text-slate-900 whitespace-pre-wrap break-all">
                      {showText ? content.content : '•'.repeat(Math.min(48, (content.content || '').length))}
                    </div>
                    <button onClick={() => setShowText((v) => !v)} className="absolute top-2 right-2 text-xs bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
                      {showText ? <><EyeOff className="h-3.5 w-3.5" /> Nascondi</> : <><Eye className="h-3.5 w-3.5" /> Mostra</>}
                    </button>
                  </div>
                  <Button onClick={copyContent} className="mt-3 w-full text-white" style={{ backgroundColor: primary }}>
                    {copied ? (<><Check className="h-4 w-4 mr-1.5" /> Copiato</>) : (<><Copy className="h-4 w-4 mr-1.5" /> Copia negli appunti</>)}
                  </Button>
                </div>
              )}
              {meta.kind === 'url' && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-sm break-all" style={{ color: primary }}>{content.content}</div>
                  <div className="flex gap-2">
                    <Button asChild className="flex-1 text-white" style={{ backgroundColor: primary }}>
                      <a href={content.content} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" /> Apri URL</a>
                    </Button>
                    <Button onClick={copyContent} variant="outline">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
                  </div>
                </div>
              )}
              {meta.kind === 'files' && (
                <div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-center gap-3">
                    <div className="h-10 w-10 rounded flex items-center justify-center" style={{ backgroundColor: primary + '18' }}><Download className="h-5 w-5" style={{ color: primary }} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{content.file_name}</div>
                      <div className="text-xs text-slate-500">{content.file_mime}{content.file_size ? ` · ${(content.file_size / 1024 / 1024).toFixed(2)} MB` : ''}</div>
                    </div>
                  </div>
                  <Button onClick={downloadFile} className="mt-3 w-full text-white" style={{ backgroundColor: primary }}><Download className="h-4 w-4 mr-1.5" /> Scarica file</Button>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-slate-100 text-xs text-slate-500">
                <div className="flex items-center justify-between">
                  <span>Questa visualizzazione è stata registrata e conteggiata.</span>
                  {meta.deletable_by_viewer && (
                    <button onClick={doDelete} className="inline-flex items-center gap-1 text-red-600 hover:text-red-700">
                      <Trash2 className="h-3.5 w-3.5" /> Distruggi ora
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        <div className="mt-6 text-center text-xs text-slate-500">
          Servizio fornito da <span className="font-semibold text-slate-700">{brand?.name || 'Secure Push'}</span>
        </div>
      </div>
    </div>
  );
}
